<template>
  <div class="flex flex-col gap-2">
    <div class="relative">
      <Search class="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input v-model="searchQuery" class="pl-8" placeholder="Search name or email" :is-error="Boolean(searchError)" />
    </div>

    <Alert v-if="searchError" variant="destructive">
      <AlertDescription>{{ searchError }}</AlertDescription>
    </Alert>

    <div v-if="isLoadingUsers" class="flex flex-col gap-2">
      <Skeleton v-for="index in 3" :key="index" class="h-11 w-full rounded-md" />
    </div>

    <div v-else-if="searchQuery.trim() && filteredUsers.length === 0" class="rounded-md border px-3 py-2 text-xs text-muted-foreground">
      No users found.
    </div>

    <div v-else-if="filteredUsers.length > 0" class="flex max-h-56 flex-col gap-1 overflow-y-auto">
      <button v-for="user in filteredUsers" :key="user.id" type="button" class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50" :disabled="conversationStore.isLoading" @click="openConversation(user)">
        <Avatar size="sm">
          <AvatarImage v-if="user.profile_photo_url" :src="user.profile_photo_url" />
          <AvatarFallback>{{ userInitials(user) }}</AvatarFallback>
        </Avatar>

        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm font-medium">{{ user.name || `User #${user.id}` }}</span>
          <span class="block truncate text-xs text-muted-foreground">{{ user.email }}</span>
        </span>

        <MessageCircle class="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>
    </div>
  </div>
</template>

<script setup>
import axios from 'axios'
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { MessageCircle, Search } from 'lucide-vue-next'
import { Alert, AlertDescription } from '../../../../../../../resources/js/components/ui/alert/index.js'
import { Avatar, AvatarFallback, AvatarImage } from '../../../../../../../resources/js/components/ui/avatar/index.js'
import { Input } from '../../../../../../../resources/js/components/ui/input/index.js'
import { Skeleton } from '../../../../../../../resources/js/components/ui/skeleton/index.js'
import { useConversationStore } from '../../stores/conversationStore.js'

const props = defineProps({
  currentUserId: {
    type: [Number, String, null],
    default: null,
  },
})

const emit = defineEmits(['conversationCreateStarted', 'conversationCreated', 'conversationCreateFailed'])

const router = useRouter()
const conversationStore = useConversationStore()

const searchQuery = ref('')
const users = ref([])
const isLoadingUsers = ref(false)
const searchError = ref('')
const searchRequestId = ref(0)
let searchDebounce = null

const filteredUsers = computed(() => {
  const currentUserId = Number(props.currentUserId)

  return users.value
    .filter((user) => Number(user.id) !== currentUserId)
    .slice(0, 8)
})

const searchUsers = async () => {
  const query = searchQuery.value.trim()
  const requestId = searchRequestId.value + 1
  searchRequestId.value = requestId

  if (!query) {
    users.value = []
    searchError.value = ''
    isLoadingUsers.value = false
    return
  }

  isLoadingUsers.value = true
  searchError.value = ''

  try {
    const { data } = await axios.get('/api/v1/users/search', {
      params: {
        search: query,
        not_paginated: true,
        order_by: 'name',
        order_direction: 'asc',
      },
    })

    if (requestId !== searchRequestId.value) {
      return
    }

    users.value = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])
  } catch (error) {
    if (requestId !== searchRequestId.value) {
      return
    }

    searchError.value = errorMessageFrom(error, 'Unable to load users.')
  } finally {
    if (requestId === searchRequestId.value) {
      isLoadingUsers.value = false
    }
  }
}

const openConversation = async (user) => {
  const userId = Number(user?.id)

  if (!Number.isInteger(userId) || userId <= 0 || conversationStore.isLoading) {
    return
  }

  searchError.value = ''
  emit('conversationCreateStarted')

  try {
    const conversation = await conversationStore.createConversation({
      type: 'direct',
      participant_user_ids: [userId],
    })

    if (!conversation?.id) {
      return
    }

    searchQuery.value = ''
    await router.push({ name: 'ChatShowPage', params: { conversation: conversation.id } })
    emit('conversationCreated', conversation)
  } catch (error) {
    searchError.value = errorMessageFrom(error, 'Unable to open conversation.')
    emit('conversationCreateFailed', searchError.value)
  }
}

const userInitials = (user) => {
  if (user?.initials) {
    return user.initials
  }

  return String(user?.name || 'U')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('') || 'U'
}

const errorMessageFrom = (error, fallback) => {
  const validationErrors = error?.response?.data?.errors

  if (validationErrors) {
    const messages = Object.values(validationErrors).flat().filter(Boolean)

    if (messages.length > 0) {
      return messages.join(' ')
    }
  }

  return error?.response?.data?.message || error?.message || fallback
}

watch(
  searchQuery,
  () => {
    window.clearTimeout(searchDebounce)
    searchDebounce = window.setTimeout(searchUsers, 250)
  }
)

onUnmounted(() => {
  window.clearTimeout(searchDebounce)
})
</script>
